import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface MessagesByDay {
  date: string;
  count: number;
}

interface GlobalMessagesTimeChartProps {
  data: MessagesByDay[];
  totalMessages: number;
  isLoading?: boolean;
}

const chartConfig = {
  count: {
    label: 'Mensagens',
    color: 'hsl(var(--chart-1))',
  },
};

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function GlobalMessagesTimeChart({ data, totalMessages, isLoading }: GlobalMessagesTimeChartProps) {
  const formattedData = data.map(item => ({
    ...item,
    dateLabel: format(parseISO(item.date), 'dd/MM', { locale: ptBR }),
  }));

  const periodMessages = data.reduce((sum, d) => sum + d.count, 0);
  const avgPerDay = data.length > 0 ? Math.round(periodMessages / data.length) : 0;

  // Find peak day
  const peakDay = data.reduce((max, d) => d.count > max.count ? d : max, { date: '', count: 0 });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Volume de Mensagens
            </CardTitle>
            <CardDescription>Mensagens por dia no período</CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{formatNumber(periodMessages)}</p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3" />
              <span>~{formatNumber(avgPerDay)}/dia</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : data.length === 0 || data.every(d => d.count === 0) ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhuma mensagem no período selecionado
          </div>
        ) : (
          <>
            <ChartContainer config={chartConfig} className="h-[220px] w-full">
              <AreaChart data={formattedData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="messagesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="dateLabel" 
                  tickLine={false} 
                  axisLine={false}
                  tickMargin={8}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  tickLine={false} 
                  axisLine={false}
                  tickMargin={8}
                  allowDecimals={false}
                  tickFormatter={formatNumber}
                />
                <ChartTooltip 
                  content={<ChartTooltipContent />}
                  labelFormatter={(value) => value}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--chart-1))"
                  strokeWidth={2}
                  fill="url(#messagesGradient)"
                />
              </AreaChart>
            </ChartContainer>
            {peakDay.count > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Pico: {formatNumber(peakDay.count)} mensagens em {format(parseISO(peakDay.date), "dd 'de' MMMM", { locale: ptBR })}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
